import { Component, ElementRef, inject, input, output, signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ThinkingMode } from '../../core/models/thinking-mode';
import { ChatAttachment } from '../../core/models/chat-message';
import { AttachmentService } from '../../core/services/attachment.service';
import { FileTextService } from '../../core/services/file-text.service';
import { OptionPickerComponent, OptionPickerOption } from '../option-picker/option-picker';

@Component({
  selector: 'app-message-composer',
  imports: [FormsModule, OptionPickerComponent],
  templateUrl: './message-composer.html',
  styleUrl: './message-composer.scss',
})
export class MessageComposerComponent {
  readonly draft = input.required<string>();
  readonly isThinking = input(false);
  readonly model = input.required<string>();
  readonly modelOptions = input.required<ReadonlyArray<OptionPickerOption<string>>>();
  readonly thinkingMode = input<ThinkingMode>('auto');
  readonly draftChange = output<string>();
  readonly messageSubmit = output<void>();
  readonly thinkingModeChange = output<ThinkingMode>();
  readonly modelChange = output<string>();
  /** Emitted when the user clicks the button while a reply is still generating. */
  readonly stop = output<void>();
  /** Emitted with the current attachments just before each submit. */
  readonly attachments = output<ChatAttachment[]>();

  private readonly attachmentsService = inject(AttachmentService);
  @ViewChild('fileInput') private fileInput: ElementRef<HTMLInputElement> | null = null;

  protected readonly pendingAttachments = signal<ChatAttachment[]>([]);
  protected readonly isDragOver = signal(false);
  /** Includes images alongside the text/document types supported by chat. */
  protected readonly acceptedTypes = `image/*,${FileTextService.ACCEPT}`;

  protected readonly thinkingOptions: ReadonlyArray<OptionPickerOption<ThinkingMode>> = [
    { value: 'auto', label: 'Auto', description: 'Agent decides whether to think' },
    { value: 'thinking', label: 'Thinking', description: 'Take more time for complex work' },
    { value: 'instant', label: 'Instant', description: 'Prioritize a quick response' },
  ];

  protected setDraft(value: string): void {
    this.draftChange.emit(value);
  }

  protected submitMessage(): void {
    if (this.isThinking()) return;
    this.attachments.emit(this.pendingAttachments());
    this.pendingAttachments.set([]);
    this.messageSubmit.emit();
  }

  /** The send button is not a submit control, so Enter must trigger submission explicitly. */
  protected onEnterKey(event: Event): void {
    event.preventDefault();
    this.submitMessage();
  }

  /** The send button doubles as a stop button while a reply is generating. */
  protected toggleSubmit(): void {
    if (this.isThinking()) {
      this.stop.emit();
    } else {
      this.submitMessage();
    }
  }

  protected openFileDialog(): void {
    this.fileInput?.nativeElement.click();
  }

  protected onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    void this.addFiles(input.files ? [...input.files] : []);
    input.value = '';
  }

  protected removeAttachment(id: string): void {
    this.pendingAttachments.update((current) => current.filter((attachment) => attachment.id !== id));
  }

  protected onDragOver(event: DragEvent): void {
    if (this.hasFiles(event)) event.preventDefault();
    this.isDragOver.set(this.hasFiles(event));
  }

  protected onDragLeave(): void {
    this.isDragOver.set(false);
  }

  protected onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver.set(false);
    if (event.dataTransfer) void this.addFiles([...event.dataTransfer.files]);
  }

  protected onPaste(event: ClipboardEvent): void {
    const files = [...(event.clipboardData?.files ?? [])].filter((file) => file.type.startsWith('image/'));
    if (files.length) void this.addFiles(files);
  }

  private hasFiles(event: DragEvent): boolean {
    return Array.from(event.dataTransfer?.types ?? []).includes('Files');
  }

  private async addFiles(files: File[]): Promise<void> {
    if (!files.length) return;
    const created = await this.attachmentsService.createAttachments(files);
    this.pendingAttachments.update((current) => [...current, ...created]);
  }
}
